import { Button, Input, Select, TextArea } from '@components'
import { NocfoPlan } from '@constants'
import { useBusinessContext } from '@context'
import { BillingInterval, ManualInvoicingMethod, useCurrentUser } from '@query'
import { useManualInvoicingRequest } from '@root/query/hooks'
import {
  AnalyticEvents,
  sendEventToGtmAndPixel,
  setAPIErrors,
  stripGlobalApiErrorKeys
} from '@utils'
import React, { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FaPaperPlane } from 'react-icons/fa'
import { useQueryClient } from '@tanstack/react-query'
import toast from 'react-hot-toast'
import styled from 'styled-components'
import {
  getManualInvoicingFormDefaults,
  MANUAL_INVOICING_FIELD_MAX
} from './manualInvoicingFormDefaults'

interface ManualInvoicingFormInputs {
  invoicing_method: ManualInvoicingMethod | ''
  email: string
  billing_address: string
  postal_code_and_city: string
  plan: NocfoPlan | ''
  billing_interval: BillingInterval | ''
  additional_info: string
}

interface Props {
  /** When set the plan is fixed and not shown (plan-order flow). */
  fixedPlan?: NocfoPlan
  /** When set the billing interval is fixed (plan-order flow). */
  fixedBillingInterval?: BillingInterval
  onSubmitSuccess?: () => void
}

const SELECTABLE_PLANS = [NocfoPlan.PLUS, NocfoPlan.PRO]
const BILLING_INTERVALS: BillingInterval[] = ['month', 'year']
const INVOICING_METHODS: ManualInvoicingMethod[] = ['EMAIL_INVOICE', 'E_INVOICE']

const MANUAL_INVOICING_TOAST_OPTIONS = { style: { zIndex: 10001 } }

const getManualInvoicingErrorData = (error: unknown) => {
  if (!error || typeof error !== 'object') {
    return undefined
  }

  const apiError = error as {
    data?: { non_field_errors?: string[] | string }
    response?: { data?: { non_field_errors?: string[] | string } }
  }

  return apiError.data ?? apiError.response?.data
}

const getManualInvoicingNonFieldError = (error: unknown): string | undefined => {
  const data = getManualInvoicingErrorData(error)
  if (!data?.non_field_errors) {
    return undefined
  }

  return Array.isArray(data.non_field_errors) ? data.non_field_errors[0] : data.non_field_errors
}

export const ManualInvoicingForm: React.FC<Props> = ({
  fixedPlan,
  fixedBillingInterval,
  onSubmitSuccess
}) => {
  const [t] = useTranslation()
  const { slug, data: business } = useBusinessContext()
  const { data: currentUser } = useCurrentUser()
  const queryClient = useQueryClient()

  const planSelectable = !fixedPlan
  const billingIntervalSelectable = !fixedBillingInterval
  const showPlanAndIntervalRow = planSelectable || billingIntervalSelectable

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting }
  } = useForm<ManualInvoicingFormInputs>({
    values: getManualInvoicingFormDefaults(business)
  })

  const { submitManualInvoicingRequest } = useManualInvoicingRequest({ slug })

  const invoicingMethodOptions = useMemo(
    () =>
      INVOICING_METHODS.map(method => ({
        value: method,
        label:
          method === 'EMAIL_INVOICE'
            ? t('components.billingSetup.cards.emailInvoice.title')
            : t('components.billingSetup.cards.eInvoice.title')
      })),
    [t]
  )

  const additionalInfoHint = t('components.billingSetup.manualInvoicing.agreedTerms')

  const onSubmit = async (values: ManualInvoicingFormInputs) => {
    const plan = fixedPlan ?? (values.plan as NocfoPlan)
    const billingInterval = fixedBillingInterval ?? (values.billing_interval as BillingInterval)
    const invoicingMethod = values.invoicing_method as ManualInvoicingMethod

    try {
      await submitManualInvoicingRequest({
        email: values.email,
        billing_address: values.billing_address,
        postal_code_and_city: values.postal_code_and_city,
        plan,
        billing_interval: billingInterval,
        invoicing_method: invoicingMethod,
        additional_info: values.additional_info || undefined
      })

      await sendEventToGtmAndPixel(AnalyticEvents.MANUAL_INVOICING_REQUESTED, currentUser, {
        plan,
        billing_interval: billingInterval,
        invoicing_method: invoicingMethod,
        lead_id: `manual-invoicing:${slug}`
      })

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: [slug] }),
        queryClient.invalidateQueries({ queryKey: ['businesses'] })
      ])

      toast.success(t('components.billingSetup.manualInvoicing.submitSuccess'))
      onSubmitSuccess?.()
    } catch (error) {
      const message = getManualInvoicingNonFieldError(error)

      if (message) {
        toast.error(message, MANUAL_INVOICING_TOAST_OPTIONS)
      } else {
        toast.error(t('general.error'), MANUAL_INVOICING_TOAST_OPTIONS)
      }

      const data = getManualInvoicingErrorData(error)
      if (data) {
        const fieldErrors = stripGlobalApiErrorKeys(data as Record<string, unknown>)
        if (Object.keys(fieldErrors).length > 0) {
          setAPIErrors(fieldErrors, setError)
        }
      }
    }
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)}>
      <FormSection>
        <Fields>
          <Input
            label={t('components.billingSetup.manualInvoicing.email')}
            id="manual-invoicing-email"
            required
            errors={errors.email}
            {...register('email', {
              required: { value: true, message: t('validation.required') },
              pattern: { value: /^\S+@\S+$/i, message: t('validation.invalidEmail') }
            })}
          />

          <Input
            label={t('components.billingSetup.manualInvoicing.billingAddress')}
            id="manual-invoicing-billing-address"
            required
            maxLength={MANUAL_INVOICING_FIELD_MAX.billingAddress}
            errors={errors.billing_address}
            {...register('billing_address', {
              required: { value: true, message: t('validation.required') }
            })}
          />

          <Input
            label={t('components.billingSetup.manualInvoicing.postalCodeAndCity')}
            id="manual-invoicing-postal-code-and-city"
            required
            maxLength={MANUAL_INVOICING_FIELD_MAX.postalCodeAndCity}
            errors={errors.postal_code_and_city}
            {...register('postal_code_and_city', {
              required: { value: true, message: t('validation.required') }
            })}
          />
        </Fields>
      </FormSection>

      <FormSection $spaced>
        <SubscriptionFields>
          {showPlanAndIntervalRow && (
            <PlanAndIntervalRow $twoColumns={planSelectable && billingIntervalSelectable}>
              {planSelectable && (
                <Select
                  label={t('components.billingSetup.manualInvoicing.plan')}
                  id="manual-invoicing-plan"
                  required
                  errors={errors.plan}
                  {...register('plan', {
                    validate: value => value !== '' || t('validation.required')
                  })}
                >
                  <option value="" disabled>
                    {t('components.billingSetup.manualInvoicing.planPlaceholder')}
                  </option>
                  {SELECTABLE_PLANS.map(plan => (
                    <option key={plan} value={plan}>
                      {t(`constants.nocfoPlan.${plan}`)}
                    </option>
                  ))}
                </Select>
              )}

              {billingIntervalSelectable && (
                <Select
                  label={t('components.billingSetup.manualInvoicing.billingInterval')}
                  id="manual-invoicing-billing-interval"
                  required
                  errors={errors.billing_interval}
                  {...register('billing_interval', {
                    validate: value => value !== '' || t('validation.required')
                  })}
                >
                  <option value="" disabled>
                    {t('components.billingSetup.manualInvoicing.billingIntervalPlaceholder')}
                  </option>
                  {BILLING_INTERVALS.map(interval => (
                    <option key={interval} value={interval}>
                      {t(
                        `components.customPricingTable.${
                          interval === 'month' ? 'monthly' : 'yearly'
                        }`
                      )}
                    </option>
                  ))}
                </Select>
              )}
            </PlanAndIntervalRow>
          )}

          <Select
            label={t('components.billingSetup.manualInvoicing.invoicingMethod')}
            id="manual-invoicing-method"
            required
            errors={errors.invoicing_method}
            {...register('invoicing_method', {
              validate: value => value !== '' || t('validation.required')
            })}
          >
            <option value="" disabled>
              {t('components.billingSetup.manualInvoicing.invoicingMethodPlaceholder')}
            </option>
            {invoicingMethodOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </SubscriptionFields>
      </FormSection>

      <FormSection $spaced>
        <TextArea
          label={t('components.billingSetup.manualInvoicing.additionalInfo')}
          id="manual-invoicing-additional-info"
          rows={3}
          maxLength={MANUAL_INVOICING_FIELD_MAX.additionalInfo}
          placeholder={additionalInfoHint}
          info={additionalInfoHint}
          errors={errors.additional_info}
          {...register('additional_info')}
        />
      </FormSection>

      <SubmitRow>
        <Button
          type="submit"
          intent="primary"
          icon={<FaPaperPlane />}
          showSpinner={isSubmitting}
          disabled={isSubmitting}
        >
          {t('components.billingSetup.manualInvoicing.submit')}
        </Button>
      </SubmitRow>
    </Form>
  )
}

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  width: 100%;
  padding-top: ${({ theme }) => theme.spacing.xs}rem;
`

const FormSection = styled.section<{ $spaced?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  margin-top: ${({ theme, $spaced }) => ($spaced ? theme.spacing.md : 0)}rem;
`

const SubscriptionFields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

const PlanAndIntervalRow = styled.div<{ $twoColumns: boolean }>`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${({ theme }) => theme.spacing.xxs}rem;

  @media (min-width: 640px) {
    grid-template-columns: ${({ $twoColumns }) => ($twoColumns ? '1fr 1fr' : '1fr')};
  }
`

const Fields = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
`

const SubmitRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: ${({ theme }) => theme.spacing.sm}rem;
`
